import { Clip } from '@/domains/asset';
import { Word } from '@/domains/transcript';
import { TranscriptParagraph, SearchParagraph, SearchResult } from '@/components/molecules/Transcript/types';
import { addDecimalIfNeeded } from '@/libs/utils';
import { convertParagraphToSearchParagraph, getParagraphSearchResults } from '@/components/molecules/Transcript/utils';
import {
  getAllEditingWordOccurrences,
  getMergesTimeRanges
} from '@/components/molecules/Transcript/TranscriptInlineEdit/utils';
import { EditedWord } from '@/stores/clip';

/**
 * Applies a word edit to the clip data. Handles both single word edits and multi-word merges.
 * For single word: updates the edits and merges with the new value
 * For multiple words: creates a merge entry spanning all selected words
 */
export function applyWordEdit(clip: Clip, words: Word[], newValue: string): Clip {
  if (words.length === 0) {
    return clip;
  }

  if (words.length > 1) {
    return applyWordMerge(clip, words, newValue);
  }

  const word = words[0];
  const editedWord: EditedWord = {
    start_time: word.start_time,
    end_time: word.end_time,
    word: newValue
  };

  const currentWordEdit = clip.asset_metadata.edits?.[addDecimalIfNeeded(word.start_time)];
  const isHiddenWord = currentWordEdit && !currentWordEdit.word;

  return {
    ...clip,
    asset_metadata: {
      ...clip.asset_metadata,
      edits: {
        ...clip.asset_metadata?.edits,
        ...(isHiddenWord
          ? {}
          : {
              [addDecimalIfNeeded(word.start_time)]: editedWord
            })
      },
      merges: (clip.asset_metadata.merges ?? [])
        .filter(({ start_time }) => start_time !== editedWord.start_time)
        .concat(editedWord)
    }
  };
}

/**
 * Merges multiple words into a single word with the given value.
 * Creates a merge entry that spans from the first word's start to the last word's end.
 */
function applyWordMerge(clip: Clip, words: Word[], newValue: string): Clip {
  const startTime = words[0].start_time;
  const endTime = words[words.length - 1].end_time;

  const editedWord: EditedWord = {
    start_time: startTime,
    end_time: endTime,
    word: newValue
  };

  return {
    ...clip,
    asset_metadata: {
      ...clip.asset_metadata,
      merges: (clip.asset_metadata.merges ?? [])
        .filter(({ start_time }) => start_time !== editedWord.start_time)
        .filter(({ start_time }) => !(start_time >= editedWord.start_time && start_time <= editedWord.end_time))
        .concat(editedWord),
      edits: {
        ...clip.asset_metadata.edits,
        [editedWord.start_time]: editedWord
      }
    }
  };
}

/**
 * Hides the given words by setting their word value to empty string in edits.
 */
export function applyWordHide(clip: Clip, words: Word[]): Clip {
  if (words.length === 0) {
    return clip;
  }

  let updatedClip = clip;
  words.forEach(word => {
    const editedWord: EditedWord = {
      start_time: word.start_time,
      end_time: word.end_time,
      word: ''
    };

    updatedClip = {
      ...updatedClip,
      asset_metadata: {
        ...updatedClip.asset_metadata,
        edits: {
          ...updatedClip.asset_metadata?.edits,
          [addDecimalIfNeeded(word.start_time)]: editedWord
        }
      }
    };
  });

  return updatedClip;
}

/**
 * Shows the given words by removing their entries from edits.
 */
export function applyWordShow(clip: Clip, words: Word[]): Clip {
  if (words.length === 0) {
    return clip;
  }

  const edits = { ...clip.asset_metadata.edits };
  words.forEach(({ start_time }) => {
    delete edits[start_time];
  });

  return {
    ...clip,
    asset_metadata: {
      ...clip.asset_metadata,
      edits
    }
  };
}

/**
 * Corrects all occurrences of the editing content in the transcript.
 * Handles both single word corrections and multi-word merge corrections.
 */
export function applyCorrectAll(
  clip: Clip,
  paragraphs: TranscriptParagraph[],
  editingContent: string,
  newValue: string,
  selectedWords: Word[]
): Clip {
  const content = paragraphs.map((paragraph, index) =>
    convertParagraphToSearchParagraph(paragraph.words, index, paragraph.segmentId)
  );
  const foundParagraphs: SearchResult[] = content.reduce((acc: SearchResult[], el: SearchParagraph) => {
    return [...acc, ...getParagraphSearchResults(el, editingContent)];
  }, []);

  let updatedClip = clip;

  if (selectedWords.length > 1) {
    const ranges = getMergesTimeRanges(foundParagraphs);
    ranges.forEach(r => {
      const startTime = r.startTime;
      const endTime = r.endTime;

      const editedWord: EditedWord = {
        start_time: startTime,
        end_time: endTime,
        word: newValue
      };

      updatedClip = {
        ...updatedClip,
        asset_metadata: {
          ...updatedClip.asset_metadata,
          merges: (updatedClip.asset_metadata.merges ?? [])
            .filter(({ start_time }) => start_time !== editedWord.start_time)
            .filter(({ start_time }) => !(start_time >= editedWord.start_time && start_time <= editedWord.end_time))
            .concat(editedWord),
          edits: {
            ...updatedClip.asset_metadata.edits,
            [editedWord.start_time]: editedWord
          }
        }
      };
    });
  } else {
    const editingWordOccurrences = getAllEditingWordOccurrences(foundParagraphs, editingContent);
    const regex = new RegExp(editingContent, 'gi');

    editingWordOccurrences.forEach(w => {
      const replacedContent = w.content.replace(regex, newValue);
      updatedClip = applyWordEdit(updatedClip, [w as unknown as Word], replacedContent);
    });
  }

  return updatedClip;
}
